import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Box, Grid, Stack } from "@mui/system";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  setSelectedBrandConfigs,
  setSelectedBusinessAdvisorDestinationTableId,
  setSelectedFlowDestinationTableId,
  setSelectedProcessSettingMode,
  setSelectedUnifierDestinationTableId,
} from "../../../../../features/dataCollectionBrandsSlice";
import SwapCallsIcon from "@mui/icons-material/SwapCalls";
const fetcherFlowConfigs = [
  { configName: "Brand Performance", destinationKey: "Daily" },
  { configName: "User Performance", destinationKey: "User" },
  { configName: "Monthly Brand Performance", destinationKey: "Monthly" },
];

const unifierFlowConfigs = [
  { configName: "Brand Performance", destinationTableIds: [2] },
  { configName: "User Performance", destinationTableIds: [4] },
  { configName: "Monthly Brand Performance", destinationTableIds: [1] },
];

const advisorFlowConfigs = [
  { configName: "Brand Performance", destinationTableIds: [2] },
  { configName: "User Performance", destinationTableIds: [3] },
  { configName: "Monthly Brand Performance", destinationTableIds: [1] },
];

const ProcessSetting = () => {
  const [expandedInput, setExpandedInput] = useState<string[]>([]);
  const hasInitializedFlowSelection = useRef(false);
  const hasInitializedReportSelection = useRef(false);
  const dispatch = useDispatch();
  const {
    brandDataInModal,
    startDate,
    endDate,
    selectedBrandConfigs,
    selectedAction,
    selectedProcessSettingMode,
    selectedFlowDestinationTableId,
    destinationTableData,
  } = useSelector((state: any) => state.dataCollectionBrands);
  const isFetcherByReport =
    selectedAction === "Fetcher" && selectedProcessSettingMode === "By Report";
  const isSharedDestinationFlow =
    selectedAction === "Unifier" ||
    selectedAction === "business_advisor" ||
    selectedAction === "unifier_business_advisor";
  const shouldShowFlowOnlyHelper =
    selectedAction === "Unifier" ||
    selectedAction === "business_advisor" ||
    selectedAction === "unifier_business_advisor";
  const hasSavedFlowSelection = selectedFlowDestinationTableId.length > 0;

  const handleCheckboxChange =
    (brandId: string, brandName: string, configName: string, tableId: string) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(
        setSelectedBrandConfigs({
          brandId,
          brandName,
          configName,
          tableId,
          checked: event.target.checked,
        }),
      );
    };

  const isChecked = (brandId: string, tableId: string) => {
    return (
      selectedBrandConfigs
        ?.find((b: any) => b.brandId === brandId)
        ?.selectedConfigs?.some((c: any) => c.tableId === tableId) || false
    );
  };

  const getBrandReportSelection = useCallback((brand: any) => {
    return {
      brandId: brand.brandId,
      brandName: brand.brandName,
      selectedConfigs: (brand.row_output_table_data || []).map(
        (tableData: any) => ({
          configName: tableData.config_name,
          tableId: tableData.table_id,
        }),
      ),
    };
  }, []);

  const allReportSelections = useMemo(() => {
    return (brandDataInModal || [])
      .map((brand: any) => getBrandReportSelection(brand))
      .filter((brand: any) => brand.selectedConfigs.length > 0);
  }, [brandDataInModal, getBrandReportSelection]);

  const allReportOptions = useMemo(() => {
    return allReportSelections.flatMap((brand: any) =>
      brand.selectedConfigs.map((config: any) => ({
        brandId: brand.brandId,
        tableId: config.tableId,
      })),
    );
  }, [allReportSelections]);

  const areAllReportOptionsChecked = (brand: any) => {
    const reportOptions = brand.row_output_table_data || [];
    return (
      reportOptions.length > 0 &&
      reportOptions.every((tableData: any) =>
        isChecked(brand.brandId, tableData.table_id),
      )
    );
  };

  const isBrandReportSelectAllIndeterminate = (brand: any) => {
    return (
      !areAllReportOptionsChecked(brand) &&
      (brand.row_output_table_data || []).some((tableData: any) =>
        isChecked(brand.brandId, tableData.table_id),
      )
    );
  };

  const isGlobalReportSelectAllChecked =
    allReportOptions.length > 0 &&
    allReportOptions.every((option: any) =>
      isChecked(option.brandId, option.tableId),
    );

  const isGlobalReportSelectAllIndeterminate =
    !isGlobalReportSelectAllChecked &&
    allReportOptions.some((option: any) =>
      isChecked(option.brandId, option.tableId),
    );

  const getDestinationTableIds = useCallback(
    (brandId: string, destinationKey: string) => {
      return (destinationTableData || [])
        .filter((item: any) => {
          const destinationTables = String(
            item.destination_tables || "",
          ).toLowerCase();
          return (
            String(item.brand_id) === String(brandId) &&
            destinationTables.includes(destinationKey.toLowerCase())
          );
        })
        .map((item: any) => item.raw_output_table_name)
        .filter(Boolean);
    },
    [destinationTableData],
  );

  const getFlowConfigs = useCallback(() => {
    if (selectedAction === "Unifier") {
      return unifierFlowConfigs;
    }
    if (
      selectedAction === "business_advisor" ||
      selectedAction === "unifier_business_advisor"
    ) {
      return advisorFlowConfigs;
    }
    return fetcherFlowConfigs;
  }, [selectedAction]);

  const getBrandFlowOptions = useCallback(
    (brand: any) => {
      return getFlowConfigs()
        .map((config: any) => {
          const destinationTableIds =
            config.destinationTableIds ||
            getDestinationTableIds(brand.brandId, config.destinationKey);
          return {
            brandId: brand.brandId,
            brandName: brand.brandName,
            configName: config.configName,
            destinationKey: config.destinationKey,
            destinationTableIds,
          };
        })
        .filter((option) => option.destinationTableIds.length > 0);
    },
    [getDestinationTableIds, getFlowConfigs],
  );

  const allFlowOptions = useMemo(() => {
    return (brandDataInModal || []).flatMap((brand: any) =>
      getBrandFlowOptions(brand),
    );
  }, [brandDataInModal, getBrandFlowOptions]);

  const isFlowChecked = (brandId: string, configName: string) => {
    return selectedFlowDestinationTableId.some(
      (c: any) => c.brandId === brandId && c.configName === configName,
    );
  };

  const areAllOptionsChecked = (options: any[]) => {
    return (
      options.length > 0 &&
      options.every((option: any) =>
        isFlowChecked(option.brandId, option.configName),
      )
    );
  };

  const isGlobalSelectAllChecked = areAllOptionsChecked(allFlowOptions);

  const isGlobalSelectAllIndeterminate =
    !isGlobalSelectAllChecked &&
    allFlowOptions.some((option: any) =>
      isFlowChecked(option.brandId, option.configName),
    );

  const handleFlowCheckboxChange =
    (
      brandId: string,
      brandName: string,
      configName: string,
      destinationTableIds: number[],
    ) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(
        setSelectedFlowDestinationTableId({
          brandId,
          brandName,
          configName,
          destinationTableIds,
          checked: event.target.checked,
        }),
      );
    };

  const handleGlobalReportSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(
      setSelectedBrandConfigs(event.target.checked ? allReportSelections : []),
    );
  };

  const handleGlobalSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(
      setSelectedFlowDestinationTableId(
        event.target.checked ? allFlowOptions : [],
      ),
    );
  };

  const handleBrandReportSelectAllChange =
    (brand: any) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const brandSelection = getBrandReportSelection(brand);
      const otherBrandSelections = selectedBrandConfigs.filter(
        (selection: any) => String(selection.brandId) !== String(brand.brandId),
      );
      dispatch(
        setSelectedBrandConfigs(
          event.target.checked && brandSelection.selectedConfigs.length > 0
            ? [...otherBrandSelections, brandSelection]
            : otherBrandSelections,
        ),
      );
    };

  const handleBrandSelectAllChange =
    (brand: any) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const brandOptions = getBrandFlowOptions(brand);
      const otherBrandSelections = selectedFlowDestinationTableId.filter(
        (selection: any) => String(selection.brandId) !== String(brand.brandId),
      );
      dispatch(
        setSelectedFlowDestinationTableId(
          event.target.checked
            ? [...otherBrandSelections, ...brandOptions]
            : otherBrandSelections,
        ),
      );
    };

  const isSharedFlowConfigChecked = (configName: string) => {
    const configOptions = allFlowOptions.filter(
      (option: any) => option.configName === configName,
    );
    return areAllOptionsChecked(configOptions);
  };

  const isSharedFlowConfigIndeterminate = (configName: string) => {
    const configOptions = allFlowOptions.filter(
      (option: any) => option.configName === configName,
    );
    return (
      !areAllOptionsChecked(configOptions) &&
      configOptions.some((option: any) =>
        isFlowChecked(option.brandId, option.configName),
      )
    );
  };

  const handleSharedFlowCheckboxChange =
    (configName: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const otherSelections = selectedFlowDestinationTableId.filter(
        (selection: any) => selection.configName !== configName,
      );
      const configOptions = allFlowOptions.filter(
        (option: any) => option.configName === configName,
      );
      dispatch(
        setSelectedFlowDestinationTableId(
          event.target.checked
            ? [...otherSelections, ...configOptions]
            : otherSelections,
        ),
      );
    };

  const handleModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    mode: any,
  ) => {
    if (!mode) return;
    dispatch(setSelectedProcessSettingMode(mode));
    dispatch(setSelectedBrandConfigs([]));
    dispatch(setSelectedFlowDestinationTableId([]));
    dispatch(setSelectedUnifierDestinationTableId([]));
    dispatch(setSelectedBusinessAdvisorDestinationTableId([]));
    hasInitializedReportSelection.current = mode !== "By Report";
    hasInitializedFlowSelection.current = mode !== "By Flow";
  };

  const handleChangeInput =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedInput((prev) =>
        isExpanded
          ? Array.from(new Set([...prev, panel]))
          : prev.filter((expandedPanel) => expandedPanel !== panel),
      );
    };

  const formatDateForUI = (dateStr: string) => {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr; // fallback if invalid
    return date.toLocaleDateString("en-GB"); // dd/mm/yyyy format
  };

  const formatConfigNameForUI = (configName: string) => {
    const trimmedConfigName = configName.trim();
    const words = trimmedConfigName.split(/[\s_]+/).filter(Boolean);
    if (words.length <= 10 && trimmedConfigName.length <= 30) {
      return trimmedConfigName;
    }
    return `${trimmedConfigName.slice(0, 30)}...`;
  };

  useEffect(() => {
    if (
      selectedAction !== "Fetcher" &&
      selectedProcessSettingMode !== "By Flow"
    ) {
      dispatch(setSelectedProcessSettingMode("By Flow"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAction, selectedProcessSettingMode]);

  useEffect(() => {
    hasInitializedFlowSelection.current = false;
    hasInitializedReportSelection.current = false;
  }, [selectedAction]);

  useEffect(() => {
    if (!isFetcherByReport) return;
    setExpandedInput(
      (brandDataInModal || []).map(
        (_brand: any, index: number) => `panel${index}`,
      ),
    );
    if (
      !hasInitializedReportSelection.current &&
      selectedBrandConfigs.length === 0 &&
      allReportSelections.length > 0
    ) {
      dispatch(setSelectedBrandConfigs(allReportSelections));
      hasInitializedReportSelection.current = true;
    }
  }, [
    allReportSelections,
    brandDataInModal,
    dispatch,
    isFetcherByReport,
    selectedBrandConfigs.length,
  ]);

  useEffect(() => {
    if (selectedProcessSettingMode !== "By Flow") return;
    setExpandedInput(
      isSharedDestinationFlow
        ? ["sharedDestinationPanel"]
        : (brandDataInModal || []).map(
            (_brand: any, index: number) => `panel${index}`,
          ),
    );
    if (
      !hasInitializedFlowSelection.current &&
      !hasSavedFlowSelection &&
      allFlowOptions.length > 0
    ) {
      dispatch(setSelectedFlowDestinationTableId(allFlowOptions));
      hasInitializedFlowSelection.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allFlowOptions,
    brandDataInModal,
    hasSavedFlowSelection,
    isSharedDestinationFlow,
    selectedProcessSettingMode,
  ]);

  return (
    <Box>
      <Stack flexDirection={"row"} gap={2} mt={2}>
        <Typography fontWeight="500">
          Start Date:{" "}
          <Typography component="span">{formatDateForUI(startDate)}</Typography>
        </Typography>
        <Typography fontWeight="500">
          End Date:{" "}
          <Typography component="span">{formatDateForUI(endDate)}</Typography>
        </Typography>
      </Stack>
      <Box mt={2}>
        <Stack
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Typography fontWeight={500}>Run Settings: </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="secondary"
            value={selectedProcessSettingMode}
            onChange={handleModeChange}
            sx={{
              backgroundColor: "grey.200",
              borderRadius: "8px",
              padding: "3px",
              border: "none",
              "& .MuiToggleButtonGroup-grouped": {
                border: "none !important",
                borderRadius: "6px !important",
              },
              "& .MuiToggleButton-root": {
                border: "none",
                px: 2,
                py: 0.75,
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.875rem",
                color: "text.disabled",
                backgroundColor: "transparent",
                "&.Mui-selected": {
                  backgroundColor: "background.paper",
                  color: "text.primary",
                  boxShadow: "0px 1px 3px rgba(0,0,0,0.15)",
                  "&:hover": {
                    backgroundColor: "background.paper",
                  },
                },
                "&.Mui-disabled": {
                  border: "none",
                  color: "text.disabled",
                },
              },
            }}
          >
            <ToggleButton value="By Flow">
              <SwapCallsIcon fontSize="small" sx={{ mr: 1 }} />
              BY FLOW
            </ToggleButton>
            <ToggleButton
              value="By Report"
              disabled={selectedAction !== "Fetcher"}
            >
              <AssignmentIcon fontSize="small" sx={{ mr: 1 }} />
              BY REPORTS
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        {shouldShowFlowOnlyHelper && (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              px: 1.5,
              py: 1,
              bgcolor: "info.50", // or use alpha: "rgba(var(--mui-palette-info-mainChannel) / 0.08)"
              borderLeft: "3px solid",
              borderColor: "info.main",
              borderRadius: "0 4px 4px 0",
            }}
          >
            <InfoOutlinedIcon
              fontSize="small"
              color="info"
              sx={{ mt: "1px", flexShrink: 0, fontSize: 16 }}
            />
            <Typography variant="caption" color="info.main" lineHeight={1.5}>
              <strong style={{ fontWeight: 500 }}>By Reports</strong> is only
              available for Fetcher — it remains disabled for all other
              processes. <br />
              This process can run by flow only.
            </Typography>
          </Box>
        )}
        <FormControlLabel
          sx={{ mt: 2 }}
          label="Select All"
          control={
            <Checkbox
              color="secondary"
              checked={
                isFetcherByReport
                  ? isGlobalReportSelectAllChecked
                  : isGlobalSelectAllChecked
              }
              indeterminate={
                isFetcherByReport
                  ? isGlobalReportSelectAllIndeterminate
                  : isGlobalSelectAllIndeterminate
              }
              onChange={
                isFetcherByReport
                  ? handleGlobalReportSelectAllChange
                  : handleGlobalSelectAllChange
              }
            />
          }
        />

        <Stack gap={2} mt={2}>
          <Grid container spacing={2}>
            <Grid size={12}>
              {isSharedDestinationFlow && !isFetcherByReport ? (
                <Accordion
                  expanded={expandedInput.includes("sharedDestinationPanel")}
                  onChange={handleChangeInput("sharedDestinationPanel")}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="shared-destination-panel-content"
                    id="shared-destination-panel-header"
                  >
                    <Typography component="span">
                      {(brandDataInModal || [])
                        .map((brand: any) => brand.brandName)
                        .join(", ")}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={1}>
                      <Grid size={12}>
                        <FormControlLabel
                          label="Select All"
                          control={
                            <Checkbox
                              color="secondary"
                              checked={isGlobalSelectAllChecked}
                              indeterminate={isGlobalSelectAllIndeterminate}
                              onChange={handleGlobalSelectAllChange}
                            />
                          }
                        />
                      </Grid>
                      {getFlowConfigs().map((config: any) => {
                        const configOptions = allFlowOptions.filter(
                          (option: any) =>
                            option.configName === config.configName,
                        );
                        return (
                          <Grid key={`shared-${config.configName}`} size={4}>
                            <FormControlLabel
                              label={`${config.configName}`}
                              control={
                                <Checkbox
                                  color="secondary"
                                  checked={isSharedFlowConfigChecked(
                                    config.configName,
                                  )}
                                  indeterminate={isSharedFlowConfigIndeterminate(
                                    config.configName,
                                  )}
                                  disabled={configOptions.length === 0}
                                  onChange={handleSharedFlowCheckboxChange(
                                    config.configName,
                                  )}
                                />
                              }
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ) : (
                brandDataInModal?.map((brand: any, index: any) => {
                  const brandFlowOptions = getBrandFlowOptions(brand);
                  const isBrandSelectAllChecked =
                    areAllOptionsChecked(brandFlowOptions);
                  const isBrandSelectAllIndeterminate =
                    !isBrandSelectAllChecked &&
                    brandFlowOptions.some((option: any) =>
                      isFlowChecked(option.brandId, option.configName),
                    );

                  return (
                    <Accordion
                      key={brand.brandId}
                      expanded={expandedInput.includes(`panel${index}`)}
                      onChange={handleChangeInput(`panel${index}`)}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`panel${index}-content`}
                        id={`panel${index}-header`}
                      >
                        <Typography
                          component="span"
                          sx={{ width: "33%", flexShrink: 0 }}
                        >
                          {brand.brandName}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack flexDirection={"column"}>
                          {isFetcherByReport ? (
                            <Grid container spacing={1}>
                              <Grid size={12}>
                                <FormControlLabel
                                  label="Select All"
                                  control={
                                    <Checkbox
                                      color="secondary"
                                      checked={areAllReportOptionsChecked(
                                        brand,
                                      )}
                                      indeterminate={isBrandReportSelectAllIndeterminate(
                                        brand,
                                      )}
                                      onChange={handleBrandReportSelectAllChange(
                                        brand,
                                      )}
                                    />
                                  }
                                />
                              </Grid>
                              {brand.row_output_table_data.map(
                                (tableData: any) => (
                                  <Grid key={tableData.table_id} size={4}>
                                    <FormControlLabel
                                      id={`outlined-${tableData.config_name
                                        .toLowerCase()
                                        .replace(" ", "-")}`}
                                      label={
                                        <Typography
                                          component="span"
                                          title={`${tableData.config_name} (${tableData.table_id})`}
                                          sx={{
                                            display: "inline-block",
                                            maxWidth: "220px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            verticalAlign: "middle",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {`${formatConfigNameForUI(
                                            tableData.config_name,
                                          )} (${tableData.table_id})`}
                                        </Typography>
                                      }
                                      control={
                                        <Checkbox
                                          color="secondary"
                                          onChange={handleCheckboxChange(
                                            brand.brandId,
                                            brand.brandName,
                                            tableData.config_name,
                                            tableData.table_id,
                                          )}
                                          checked={isChecked(
                                            brand.brandId,
                                            tableData.table_id,
                                          )}
                                        />
                                      }
                                    />
                                  </Grid>
                                ),
                              )}
                            </Grid>
                          ) : (
                            <Grid container spacing={1}>
                              <Grid size={12}>
                                <FormControlLabel
                                  label="Select All"
                                  control={
                                    <Checkbox
                                      color="secondary"
                                      checked={isBrandSelectAllChecked}
                                      indeterminate={
                                        isBrandSelectAllIndeterminate
                                      }
                                      onChange={handleBrandSelectAllChange(
                                        brand,
                                      )}
                                    />
                                  }
                                />
                              </Grid>
                              {getFlowConfigs().map((config: any) => {
                                const destinationTableIds =
                                  config.destinationTableIds ||
                                  getDestinationTableIds(
                                    brand.brandId,
                                    config.destinationKey,
                                  );
                                return (
                                  <Grid
                                    key={`${brand.brandId}-${config.configName}`}
                                    size={4}
                                  >
                                    <FormControlLabel
                                      label={`${config.configName}`}
                                      control={
                                        <Checkbox
                                          color="secondary"
                                          checked={isFlowChecked(
                                            brand.brandId,
                                            config.configName,
                                          )}
                                          disabled={
                                            destinationTableIds.length === 0
                                          }
                                          onChange={handleFlowCheckboxChange(
                                            brand.brandId,
                                            brand.brandName,
                                            config.configName,
                                            destinationTableIds,
                                          )}
                                        />
                                      }
                                    />
                                  </Grid>
                                );
                              })}
                            </Grid>
                          )}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  );
                })
              )}
            </Grid>
          </Grid>
        </Stack>
      </Box>
    </Box>
  );
};

export default ProcessSetting;
